import React, { useState, useEffect } from 'react';
import { 
  Grid, 
  Paper, 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CardHeader,
  List,
  ListItem,
  ListItemText,
  Divider,
  Button,
  IconButton,
  CircularProgress,
  Alert,
  Tooltip,
  TextField,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  useTheme
} from '@mui/material';
import { 
  AccountBalance as AccountBalanceIcon,
  Add as AddIcon,
  Info as InfoIcon,
  Receipt as ReceiptIcon,
  Computer as ComputerIcon,
  Message as MessageIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon
} from '@mui/icons-material';
import { DateRange } from '@mui/icons-material';
import { format } from 'date-fns';
import DashboardLayout from '../../components/DashboardLayout';
import { useAuth } from '../../hooks/useAuth';
import financeService from '../../services/financeService';

const Finance = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [financialData, setFinancialData] = useState({
    balance: 0,
    last_week_transactions: { sum: 0, transaction_count: 0, start_date: null, end_date: null },
    recent_transactions: []
  });
  const [openDepositDialog, setOpenDepositDialog] = useState(false);
  const [depositAmount, setDepositAmount] = useState('');
  const [depositDescription, setDepositDescription] = useState('');
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositError, setDepositError] = useState(null);

  useEffect(() => {
    const fetchFinancialData = async () => {
      try {
        setLoading(true);
        const data = await financeService.getFinancialSummary();
        setFinancialData(data);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching financial data:', err);
        setError('Failed to load financial data. Please try again later.');
        setLoading(false);
      }
    };

    fetchFinancialData();
  }, []);

  const handleOpenDepositDialog = () => {
    setOpenDepositDialog(true);
    setDepositAmount('');
    setDepositDescription('');
    setDepositError(null);
  };

  const handleCloseDepositDialog = () => {
    setOpenDepositDialog(false);
  };

  const handleDepositSubmit = async () => {
    try {
      setDepositLoading(true);
      setDepositError(null);

      if (!depositAmount || isNaN(depositAmount) || parseFloat(depositAmount) <= 0) {
        setDepositError('Please enter a valid amount');
        setDepositLoading(false);
        return;
      }

      const result = await financeService.addFunds(parseFloat(depositAmount), depositDescription);

      // Update balance in financial data
      setFinancialData(prevData => ({
        ...prevData,
        balance: result.balance
      }));

      // Refresh financial data to get updated transactions
      const updatedData = await financeService.getFinancialSummary();
      setFinancialData(updatedData);

      setDepositLoading(false);
      handleCloseDepositDialog();
    } catch (err) {
      console.error('Error adding funds:', err);
      setDepositError('Failed to add funds. Please try again later.');
      setDepositLoading(false);
    }
  };

  const getTransactionTypeColor = (type) => {
    switch (type) {
      case 'deposit':
        return theme.palette.success.main;
      case 'instance_rental':
        return theme.palette.warning.main;
      case 'message_generation':
        return theme.palette.info.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getTransactionTypeIcon = (type) => {
    switch (type) {
      case 'deposit':
        return <ArrowUpwardIcon fontSize="small" />;
      case 'instance_rental':
        return <ComputerIcon fontSize="small" />;
      case 'message_generation':
        return <MessageIcon fontSize="small" />;
      default:
        return <ReceiptIcon fontSize="small" />;
    }
  };

  const formatTransactionType = (type) => {
    switch (type) {
      case 'deposit':
        return 'Deposit';
      case 'instance_rental':
        return 'Instance Rental';
      case 'message_generation':
        return 'Message Generation';
      default:
        return type;
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
          <CircularProgress />
        </Box>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <Alert severity="error" sx={{ mb: 4 }}>{error}</Alert>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Finance
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your balance and view your financial information.
        </Typography>
      </Box>

      {/* Balance and Last Week Transactions Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          {/* Current Balance (reduced height) */}
          <Card sx={{ mb: 3 }}>
            <CardHeader 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AccountBalanceIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Current Balance</Typography>
                </Box>
              }
              action={
                <Button 
                  variant="contained" 
                  color="primary" 
                  startIcon={<AddIcon />}
                  onClick={handleOpenDepositDialog}
                >
                  Add Funds
                </Button>
              }
            />
            <Divider />
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', py: 2 }}>
                <Typography variant="h4" component="div" sx={{ fontWeight: 'bold', mb: 1 }}>
                  ₽{parseFloat(financialData.balance).toFixed(2)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Your current account balance
                </Typography>
              </Box>
            </CardContent>
          </Card>


        </Grid>

        {/* Last Week Transactions Card */}
        <Grid item xs={12} md={6}>
            {/* New Period Summary Card */}
            <Card>
                <CardHeader
                    title={
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <ReceiptIcon sx={{ mr: 1 }} />
                            <Typography variant="h6">Period Summary</Typography>
                        </Box>
                    }
                />
                <Divider />
                <CardContent>
                    <PeriodSummary />
                </CardContent>
            </Card>
        </Grid>
      </Grid>


      {/* Recent Transactions */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card>
            <CardHeader 
              title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ReceiptIcon sx={{ mr: 1 }} />
                  <Typography variant="h6">Recent Transactions</Typography>
                </Box>
              }
              action={
                <Button 
                  variant="text" 
                  onClick={() => {/* View all transactions */}}
                >
                  View All
                </Button>
              }
            />
            <Divider />
            <CardContent>
              {financialData.recent_transactions.length > 0 ? (
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {financialData.recent_transactions.map((transaction) => (
                        <TableRow key={transaction.id}>
                          <TableCell>
                            <Chip 
                              icon={getTransactionTypeIcon(transaction.type)}
                              label={formatTransactionType(transaction.type)}
                              size="small"
                              sx={{ 
                                backgroundColor: `${getTransactionTypeColor(transaction.type)}20`,
                                color: getTransactionTypeColor(transaction.type),
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell>{transaction.description || '-'}</TableCell>
                          <TableCell>{format(new Date(transaction.created_at), 'MMM dd, yyyy HH:mm')}</TableCell>
                          <TableCell align="right" sx={{ 
                            color: transaction.type === 'deposit' ? theme.palette.success.main : theme.palette.error.main,
                            fontWeight: 'bold'
                          }}>
                            {transaction.type === 'deposit' ? '+' : '-'}₽{Math.abs(transaction.amount).toFixed(2)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Box sx={{ p: 3, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    No recent transactions found.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add Funds Dialog */}
      <Dialog open={openDepositDialog} onClose={handleCloseDepositDialog}>
        <DialogTitle>Add Funds to Your Account</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Enter the amount you want to add to your account balance.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Amount (₽)"
            type="number"
            fullWidth
            variant="outlined"
            value={depositAmount}
            onChange={(e) => setDepositAmount(e.target.value)}
            error={!!depositError}
            helperText={depositError}
            InputProps={{
              startAdornment: <Typography sx={{ mr: 1 }}>₽</Typography>,
            }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            type="text"
            fullWidth
            variant="outlined"
            value={depositDescription}
            onChange={(e) => setDepositDescription(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDepositDialog} disabled={depositLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleDepositSubmit} 
            variant="contained" 
            color="primary"
            disabled={depositLoading}
            startIcon={depositLoading ? <CircularProgress size={20} /> : <AddIcon />}
          >
            {depositLoading ? 'Processing...' : 'Add Funds'}
          </Button>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
};

// --- Period Summary Subcomponent ---
const PeriodSummary = () => {
  const theme = useTheme();
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().slice(0, 10);
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [periodData, setPeriodData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await financeService.getPeriodSummary(startDate, endDate);
      setPeriodData(data);
      setLoading(false);
    } catch (e) {
      console.error('Failed to load period summary', e);
      setError('Failed to load period summary');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
        <TextField
          label="Start date"
          type="date"
          size="small"
          value={startDate}
          onChange={(e) => setStartDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <TextField
          label="End date"
          type="date"
          size="small"
          value={endDate}
          onChange={(e) => setEndDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
        <Button variant="contained" size="small" startIcon={<DateRange />} onClick={fetchSummary} disabled={loading}>
          {loading ? 'Loading...' : 'Apply'}
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {loading && !periodData ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : periodData ? (
        <List>
          <ListItem>
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ArrowUpwardIcon sx={{ color: theme.palette.success.main, mr: 1 }} />
                    Deposits
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.success.main }}>
                    +₽{parseFloat(periodData.deposits?.sum || 0).toFixed(2)}
                  </Typography>
                </Box>
              }
              secondary={`${periodData.deposits?.count || 0} deposits`}
            />
          </ListItem>
          <Divider component="li" />

          <ListItem>
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <ComputerIcon sx={{ color: theme.palette.warning.main, mr: 1 }} />
                    Instance Rentals
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                    -₽{parseFloat(periodData.instance_rentals?.sum || 0).toFixed(2)}
                  </Typography>
                </Box>
              }
              secondary={`${periodData.instance_rentals?.count || 0} items`}
            />
          </ListItem>
          <Divider component="li" />

          <ListItem>
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center' }}>
                    <MessageIcon sx={{ color: theme.palette.info.main, mr: 1 }} />
                    Message Generations
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'bold', color: theme.palette.error.main }}>
                    -₽{parseFloat(periodData.message_generations?.sum || 0).toFixed(2)}
                  </Typography>
                </Box>
              }
              secondary={`${periodData.message_generations?.count || 0} messages`}
            />
          </ListItem>
          <Divider component="li" />

          <ListItem>
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="body1" sx={{ fontWeight: 'bold' }}>Total</Typography>
                  <Typography variant="body1" sx={{ 
                    fontWeight: 'bold',
                    color: parseFloat(periodData.sum) >= 0 
                      ? theme.palette.success.main 
                      : theme.palette.error.main
                  }}>
                    {parseFloat(periodData.sum) >= 0 ? '+' : '-'}₽{Math.abs(parseFloat(periodData.sum)).toFixed(2)}
                  </Typography>
                </Box>
              }
              secondary={
                periodData.start_date && (
                  <Typography variant="caption" color="text.secondary">
                    {format(new Date(periodData.start_date), 'MMM dd, yyyy')} - {format(new Date(periodData.end_date), 'MMM dd, yyyy')}
                  </Typography>
                )
              }
            />
          </ListItem>
        </List>
      ) : (
        <Typography variant="body2" color="text.secondary">No data</Typography>
      )}
    </Box>
  );
};

export default Finance;
